import React from 'react';
import { Link } from 'react-router-dom';

export const AboutPage: React.FC = () => {
  return (
    <div>
      {/* Page header */}
      <section
        className="text-white text-center py-16 px-4"
        style={{ background: 'linear-gradient(135deg, #2c5530 0%, #4a7c59 100%)' }}
      >
        <h1 className="text-4xl font-semibold mb-3">About Pop-Up Yoga</h1>
        <p className="text-lg opacity-90">Bringing mindfulness and movement to unexpected places</p>
      </section>

      {/* Story + image */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-start">
            <div className="md:col-span-2 space-y-5 text-gray-700 leading-relaxed">
              <h2 className="text-3xl font-semibold text-green-800">Our Story</h2>
              <p>
                Pop-Up Yoga was born from the belief that yoga should be accessible to everyone, everywhere.
                We started with a simple idea: why limit ourselves to traditional studio spaces when the world
                is full of beautiful places to practice?
              </p>
              <p>
                Practice is, at once, deeply personal and inherently shared. We move alone, on our own mat,
                accountable for our own breath and body — and yet most of us found this practice, and keep
                finding it, because someone else showed us how, or showed up next to us. Pop-Up Yoga exists
                to make that room easier to find: connecting students looking for a place to practice with
                teachers willing to hold space for them, in parks, beaches, and unexpected places.
              </p>
              <p>
                We are infrastructure for community, not a destination in ourselves. If we do our job well,
                people spend less time in our app and more time on their mats.
              </p>

              <h3 className="text-xl font-semibold text-green-800 pt-4">What Guides Us</h3>
              <ul className="space-y-2 list-disc list-inside">
                <li>Searching for a class and finding a teacher will always be free for students</li>
                <li>Offering a free event will always be free for teachers — no platform fee stands between a teacher and a willing room</li>
                <li>Your email and phone number are never visible to other users — we collect only what the platform needs to function</li>
                <li>Technology should serve the practice, not compete with it — we optimize for a short, useful visit that ends with someone walking onto a mat</li>
              </ul>

              <h3 className="text-xl font-semibold text-green-800 pt-4">What We Offer</h3>
              <ul className="space-y-2 list-disc list-inside">
                <li>All-levels yoga classes in outdoor settings</li>
                <li>Special events and workshops</li>
                <li>SUP Yoga</li>
                <li>Live music yoga classes</li>
                <li>Private group sessions</li>
                <li>Corporate wellness programs</li>
              </ul>

              <div className="pt-4">
                <Link
                  to="/events"
                  className="inline-block bg-green-800 hover:bg-green-900 text-white font-medium px-6 py-3 rounded-md transition-colors"
                >
                  Browse Events
                </Link>
              </div>
            </div>

            <div className="md:col-span-1">
              <img
                src="/popupyoga.png"
                alt="Pop-Up Yoga session"
                className="w-full rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-semibold text-green-800 mb-4">Where We Practice</h2>
          <p className="text-gray-600 mb-10 max-w-xl mx-auto">
            We currently operate in two beautiful locations, with more on the way.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {[
              { name: 'Richmond, VA', desc: 'Parks, riverfront, and urban green spaces throughout the city' },
              { name: 'Outer Banks, NC', desc: 'Beachside sessions with ocean views and sea breezes' },
            ].map(loc => (
              <div key={loc.name} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-green-800 mb-2">📍 {loc.name}</h3>
                <p className="text-gray-600 text-sm">{loc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-gray-800 text-white text-center py-8">
        <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} Pop-Up Yoga. All rights reserved.</p>
        <p className="text-gray-500 text-xs mt-2">
          <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
          {' · '}
          <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
        </p>
      </footer>
    </div>
  );
};
